import z from 'zod';
import { optionNumberSchema, optionSchema } from '~/features/types';

// [MCV-1889] The Buying tab's filters (it searches OFFERING listings). Every field is optional:
// an empty filter lists everything.
//
// `location` is a city/ZIP picked from the geography search. The coordinates ride along with the
// option because the marketplace search takes `nearLat` / `nearLng`, not a place name.
export const offeringLocationSchema = z.object({
  value: z.string().readonly(),
  label: z.string().readonly(),
  latitude: z.number(),
  longitude: z.number(),
});

export const offeringSearchSchema = z.object({
  location: offeringLocationSchema.nullish(),
  // Licensed states the buyer needs covered.
  states: z.array(optionSchema).optional(),
  providerCategory: optionNumberSchema.nullish(),
  aoiSpecialty: optionSchema.nullish(),
  // A listing matches when it has ANY of these (DD6).
  subspecialties: z.array(optionSchema).optional(),
  // One of the buyer's own SEEKING listings; fit is scored against it (DD8).
  compareListing: optionNumberSchema.nullish(),
  sortBy: optionSchema.nullish(),
});

export type OfferingSearchValues = z.infer<typeof offeringSearchSchema>;

export const OFFERING_SORT_OPTIONS = [
  { value: 'BEST_MATCH', label: 'Best match' },
  { value: 'LOWEST_RATE', label: 'Lowest rate' },
  { value: 'MOST_RECENT', label: 'Most recent' },
];

// The filters that narrow a Buying search; sort is not one of them.
const OFFERING_FILTER_LABELS: Array<[keyof OfferingSearchValues, string]> = [
  ['location', 'Location'],
  ['states', 'Required license states'],
  ['providerCategory', 'Provider category'],
  ['aoiSpecialty', 'Specialty'],
  ['subspecialties', 'Subspecialties'],
];

export const activeOfferingFilterLabels = (filters: Partial<OfferingSearchValues>): string[] =>
  OFFERING_FILTER_LABELS.filter(([key]) => {
    const value = filters[key];
    return Array.isArray(value) ? value.length > 0 : value != null;
  }).map(([, label]) => label);
